import { useState, useCallback, useRef } from "react";
import { message } from "antd";
import { getVendorBiddingPage } from "@/apis/inquiry.api";
import type { SrmVendorBiddingVo } from "@/types/apis";

// ==================== 类型定义 ====================

export interface VendorBiddingQueryParams {
  pageNum: number;
  pageSize: number;
  biddingNo?: string;
  biddingName?: string;
  createByName?: string;
}

// ==================== 自定义Hook ====================

/**
 * 获取供应商报价数据的Hook
 * 处理分页、搜索、加载状态等逻辑
 */
export const useVendorBiddingData = () => {
  const [data, setData] = useState<SrmVendorBiddingVo[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [queryParams, setQueryParams] = useState<VendorBiddingQueryParams>({
    pageNum: 1,
    pageSize: 10,
  });

  const queryParamsRef = useRef(queryParams);

  /**
   * 获取报价数据
   */
  const fetchData = useCallback(async (params: VendorBiddingQueryParams) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getVendorBiddingPage({
        pageNum: params.pageNum,
        pageSize: params.pageSize,
        biddingNo: params.biddingNo || null,
        biddingName: params.biddingName || null,
        biddingType: null,
        biddingStatus: null,
        createBy: null,
        supplierId: null,
        materialNames: null,
      });
      if (response.data?.data) {
        setData(response.data.data.list || []);
        setTotal(response.data.data.total || 0);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "获取报价数据失败";
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 初始化数据
   */
  const initialize = useCallback(async () => {
    await fetchData(queryParamsRef.current);
  }, [fetchData]);

  /**
   * 处理分页变化
   */
  const handlePageChange = useCallback(
    async (pageNum: number, pageSize: number) => {
      const newParams = { ...queryParamsRef.current, pageNum, pageSize };
      queryParamsRef.current = newParams;
      setQueryParams(newParams);
      await fetchData(newParams);
    },
    [fetchData],
  );

  /**
   * 处理搜索
   */
  const handleSearch = useCallback(
    async (searchParams: Partial<VendorBiddingQueryParams>) => {
      const newParams = {
        ...queryParamsRef.current,
        ...searchParams,
        pageNum: 1, // 搜索时重置到第一页
      };
      queryParamsRef.current = newParams;
      setQueryParams(newParams);
      await fetchData(newParams);
    },
    [fetchData],
  );

  /**
   * 重置搜索条件
   */
  const handleReset = useCallback(async () => {
    const resetParams: VendorBiddingQueryParams = {
      pageNum: 1,
      pageSize: 10,
    };
    queryParamsRef.current = resetParams;
    setQueryParams(resetParams);
    await fetchData(resetParams);
  }, [fetchData]);

  return {
    data,
    loading,
    total,
    error,
    queryParams,
    initialize,
    handlePageChange,
    handleSearch,
    handleReset,
  };
};
